import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, MapPin, ChevronRight, Zap } from "lucide-react";
import { api } from "../lib/api";

const FORMAT_COLOR = {
  TEST: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  ODI: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  T20I: "bg-purple-500/15 text-purple-300 border-purple-500/30",
};

const HERO_IMG = "https://images.unsplash.com/photo-1512719994953-eabf50895df7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjcmlja2V0JTIwc3RhZGl1bSUyMG5pZ2h0JTIwbWF0Y2h8ZW58MHx8fHwxNzg3NTQ2MDI1fDA&ixlib=rb-4.1.0&q=85";

export default function LiveMatchesPage() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/matches").then((r) => { setMatches(r.data); setLoading(false); });
  }, []);

  const live = matches.filter((m) => m.status === "LIVE");
  const upcoming = matches.filter((m) => m.status === "UPCOMING");

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="stadium" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 hero-glow" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
          <div className="pill bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 mb-5">
            <span className="live-dot" /> Live over-by-over
          </div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-7xl leading-none tracking-tight max-w-3xl">
            Read the game. <span className="text-amber-400">Own the over.</span>
          </h1>
          <div className="mt-6 max-w-2xl rounded-2xl overflow-hidden border border-amber-500/30 shadow-2xl shadow-amber-500/10">
            <video
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_live-wicket-forecast/artifacts/xwl02upe_Elva%20Vid%20%233.mp4"
              autoPlay
              loop
              muted
              playsInline
              controls
              className="w-full h-auto block"
              data-testid="hero-video"
            />
          </div>
          <p className="mt-5 max-w-xl text-slate-300 text-base sm:text-lg">
            Predict outcomes from each ball — live — across international Tests, ODIs and T20Is. Match wits with the Claude Oracle AI and climb the arena ladder.
          </p>
          <p className="mt-4 max-w-xl text-slate-300 text-base sm:text-lg">
            Once you feel confident of tackling Goliath101 App then{" "}
            <a
              href="https://www.thecricketpitch.online"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-amber-400 font-display font-black uppercase tracking-wider hover:text-amber-300 underline underline-offset-4 decoration-amber-500/50 hover:decoration-amber-400"
              data-testid="hero-cta-cricketpitch"
            >
              [ Enter The Cricket Pitch ]
            </a>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#live-now" className="btn-primary" data-testid="hero-cta-live">
              <Zap className="w-4 h-4 inline mr-2" /> Enter live arena
            </a>
            <Link to="/leaderboard" className="btn-ghost" data-testid="hero-cta-leaderboard">
              View leaderboard
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
        <div id="live-now" className="flex items-center justify-between mb-6">
          <div>
            <div className="section-label">01 // Now on field</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl uppercase tracking-wide mt-1">Live Matches</h2>
          </div>
          <div className="text-xs font-mono-data text-slate-400 uppercase tracking-[0.24em]">{live.length} live</div>
        </div>

        {loading ? (
          <div className="text-slate-400" data-testid="matches-loading">Loading match feed…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 stagger">
            {live.map((m) => (
              <MatchCard key={m.id} match={m} />
            ))}
            {live.length === 0 && (
              <div className="tile p-8 text-slate-400" data-testid="no-live-matches">No live international matches right now. Check the schedule.</div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-14 mb-6">
          <div>
            <div className="section-label">02 // Coming up</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl uppercase tracking-wide mt-1">Upcoming</h2>
          </div>
          <Link to="/schedule" className="text-xs text-amber-400 uppercase tracking-wider font-semibold hover:underline">Full schedule →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {upcoming.map((m) => <UpcomingCard key={m.id} match={m} />)}
        </div>
      </div>
    </div>
  );
}

function MatchCard({ match }) {
  const balls = 6 - (match.balls_this_over || 0);
  return (
    <Link to={`/matches/${match.id}`} className="tile p-6 grain group block" data-testid={`match-card-${match.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`pill border ${FORMAT_COLOR[match.format] || "bg-slate-500/15 text-slate-300 border-slate-500/30"}`}>{match.format}</div>
        <div className="pill bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" data-testid="match-live-badge">
          <span className="live-dot" /> Live
        </div>
      </div>

      <div className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <MapPin className="w-3 h-3" /> {match.venue}
      </div>
      <div className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight leading-tight">
        {match.short}
      </div>
      <div className="text-slate-400 text-sm mt-0.5">{match.series}</div>

      <div className="mt-5 flex items-end justify-between">
        <div>
          <div className="section-label">{match.batting_team} • Innings {match.innings_no}</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="metric-value text-4xl text-emerald-400">{match.score}/{match.wickets}</span>
            <span className="font-mono-data text-slate-400 text-sm">({match.overs_completed}.{match.balls_this_over})</span>
          </div>
        </div>
        <div className="text-right">
          <div className="section-label">Next over in</div>
          <div className="metric-value text-2xl text-amber-400">{balls} balls</div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between text-sm">
        <span className="text-slate-300"><span className="text-slate-500">Striker:</span> {match.striker?.name} <span className="font-mono-data text-emerald-400">{match.striker?.runs}({match.striker?.balls})</span></span>
        <span className="text-amber-400 flex items-center gap-1 font-semibold uppercase text-xs tracking-wider group-hover:translate-x-1 transition-transform">
          Predict <ChevronRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}

function UpcomingCard({ match }) {
  return (
    <Link to={`/matches/${match.id}`} className="tile p-5 grain block" data-testid={`match-card-${match.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`pill border ${FORMAT_COLOR[match.format] || ""}`}>{match.format}</div>
        <div className="pill bg-sky-500/15 text-sky-300 border border-sky-500/30">Upcoming</div>
      </div>
      <div className="font-display font-black text-xl uppercase">{match.short}</div>
      <div className="text-xs text-slate-400 mt-1">{match.series}</div>
      <div className="mt-4 flex items-center justify-between text-xs">
        <span className="text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{match.venue}</span>
        <span className="font-mono-data text-amber-400">{match.starts_at || "TBA"}</span>
      </div>
    </Link>
  );
}
